<template>
  <div class="pa-4" style="max-width: 1100px; margin: 0 auto">
    <h2 class="mb-1">API Console</h2>
    <p class="text-caption mb-4" style="opacity: 0.7">
      Dev alat. Zahtjevi idu kroz isti axios kao aplikacija — token,
      <code>company</code> i <code>restaurant</code> header se dodaju automatski.
      URL je relativan na <code>{{ apiBase }}</code>.
    </p>

    <!-- Auth context -->
    <v-alert dense text type="info" class="mb-4">
      <div style="display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px">
        <span>token: <b>{{ hasToken ? "da" : "NE" }}</b></span>
        <span>user.type: <b>{{ userType || "—" }}</b></span>
        <span>state.company.id: <b>{{ companyId || "—" }}</b></span>
        <span>state.restaurant.id: <b>{{ restaurantId || "—" }}</b></span>
      </div>
    </v-alert>

    <!-- Presets -->
    <div class="mb-3">
      <span class="text-caption mr-2" style="opacity: 0.7">Brzi izbor:</span>
      <v-chip
        v-for="p in presets"
        :key="p.label"
        small
        outlined
        class="ma-1"
        @click="applyPreset(p)"
      >
        {{ p.label }}
      </v-chip>
    </div>

    <!-- Request builder -->
    <v-card outlined class="pa-4 mb-4">
      <div style="display: flex; gap: 12px; align-items: flex-start">
        <v-select
          v-model="method"
          :items="methods"
          label="Metoda"
          dense
          outlined
          hide-details
          style="max-width: 130px"
        />
        <v-text-field
          v-model="url"
          label="URL (relativno na /api)"
          placeholder="companies/24/users-delivery"
          dense
          outlined
          hide-details
          @keyup.enter="send"
        />
        <v-btn color="primary" :loading="loading" @click="send" style="height: 40px">
          Pošalji
        </v-btn>
      </div>

      <v-textarea
        v-if="bodyAllowed"
        v-model="body"
        label="Body (JSON)"
        placeholder='{ "name": "..." }'
        outlined
        dense
        rows="6"
        class="mt-4"
        hide-details="auto"
        :error="bodyError"
        :error-messages="bodyError ? 'Nevažeći JSON' : ''"
        spellcheck="false"
        style="font-family: monospace"
      />
    </v-card>

    <!-- Response -->
    <v-card v-if="response || errorText" outlined class="pa-4">
      <div
        v-if="response"
        class="mb-2"
        style="display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px"
      >
        <span>
          status:
          <b :style="{ color: statusColor }">{{ response.status }} {{ response.statusText }}</b>
        </span>
        <span>vrijeme: <b>{{ response.ms }} ms</b></span>
        <span>veličina: <b>{{ response.size }}</b></span>
      </div>

      <v-alert v-if="errorText" dense text type="error" class="mb-2">
        {{ errorText }}
      </v-alert>

      <div style="display: flex; gap: 8px" class="mb-2">
        <v-btn x-small text @click="copy(prettyBody)">Kopiraj body</v-btn>
        <v-btn x-small text @click="showHeaders = !showHeaders">
          {{ showHeaders ? "Sakrij" : "Prikaži" }} response headers
        </v-btn>
      </div>

      <pre v-if="showHeaders" class="resp">{{ prettyHeaders }}</pre>
      <pre class="resp">{{ prettyBody }}</pre>
    </v-card>

    <!-- History -->
    <v-card v-if="history.length" outlined class="pa-4 mt-4">
      <div class="text-caption mb-2" style="opacity: 0.7">
        Istorija (ova sesija)
        <v-btn x-small text @click="history = []">očisti</v-btn>
      </div>
      <div
        v-for="(h, i) in history"
        :key="i"
        class="hist"
        @click="restore(h)"
      >
        <span :style="{ color: h.ok ? '#2e7d32' : '#c62828' }">{{ h.status }}</span>
        <b class="mx-2">{{ h.method }}</b>
        <span>{{ h.url }}</span>
        <span class="ml-2" style="opacity: 0.6">{{ h.ms }} ms</span>
      </div>
    </v-card>
  </div>
</template>

<script>
// Dev-only. Not linked from any menu — open /dev/api directly.
import axiosInstance from "@/plugins/axios";
import cfg from "@/config";

const LS_KEY = "dev-api-console:last";

export default {
  name: "DevApiConsole",
  data() {
    return {
      apiBase: cfg.apiUrl,
      methods: ["GET", "POST", "PATCH", "PUT", "DELETE"],
      method: "GET",
      url: "",
      body: "",
      loading: false,
      response: null,
      errorText: "",
      showHeaders: false,
      history: [],
      presets: [
        // Ugovori/usluge/cjenovnik (11.09) — vidi docs/2026/09/11_09_2026_Network_checklist_ugovori.md
        // i bitlab #223558. Test restoran: 175 ("Roštiljnica Laguna", isti iz
        // tiket screenshota). Zamijeni <ID> / <CONTRACT_ID> / <PRICING_ID>
        // stvarnim vrijednostima prije slanja.
        {
          // Istorija ugovora restorana 175 — provjeri da svaki contract ima
          // valid_from, valid_to, status, dana_do_isteka, broj_usluga,
          // can_edit na root nivou. Status mora biti jedan od VAŽI/USKORO
          // ISTIČE/ISTEKAO/BEZ ROKA.
          label: "U1 GET ugovori restorana 175",
          method: "GET",
          url: "restaurants/175/contracts",
        },
        {
          // Novi ugovor + stavke u istom pozivu. Zamijeni service_id
          // stvarnim ID-jevima iz U4 (GET services) odgovora prije slanja —
          // ovi su samo placeholderi. Očekivano (201): poruka o zatvaranju
          // prethodnog ugovora + contract objekat sa status: "VAŽI".
          label: "U2 POST novi ugovor (restoran 175, sa stavkama)",
          method: "POST",
          url: "restaurants/175/contracts",
          body: {
            contract_number: "TEST-" + new Date().toISOString().slice(0, 10),
            contract_date: new Date().toISOString().slice(0, 10),
            valid_from: null,
            valid_to: null,
            document_url: null,
            note: "test iz /dev/api",
            payout_schedule: "MONTHLY",
            payout_threshold_amount: null,
            items: [
              { service_id: 1, paying_type: 2, amount: 12.5, cash_flow_model: "COURIER_PAYS_ON_PICKUP" },
              { service_id: 2, paying_type: 2, amount: 8.0 },
            ],
          },
        },
        {
          // Izmjena postojećeg ugovora — SAMO contract_number/valid_to/
          // document_url/note (spec: cijene i valid_from se ovdje ne mijenjaju).
          // Zamijeni <CONTRACT_ID> ID-jem ugovora iz U1 odgovora.
          label: "U3 PUT izmjena ugovora <CONTRACT_ID> (restoran 175)",
          method: "PUT",
          url: "restaurants/175/contracts/<CONTRACT_ID>",
          body: {
            contract_number: "2026-114",
            valid_to: "2026-12-31",
            document_url: null,
            note: "izmijenjeno iz /dev/api",
          },
        },
        {
          // Globalni radni spisak za dashboard (Ugovori stranica, admin).
          // Provjeri summary.istekli/uskoro/bez_ugovora i da je contracts[]
          // sortiran po hitnosti (najduže istekli prvi).
          label: "U4 GET radni spisak ugovora (expiring)",
          method: "GET",
          url: "contracts/expiring",
        },
        {
          // Šifarnik usluga + options (paying_types/payout_schedules/
          // cash_flow_models) za restoran 175 — ovo puni padajuće liste u
          // formi za novi ugovor. uses_cash_flow_model mora biti true SAMO
          // za DELIVERY_PLATFORM.
          label: "U5 GET usluge restorana 175 (+ options)",
          method: "GET",
          url: "restaurants/175/services",
        },
        {
          // Cjenovnik — istorija cijena po usluzi. is_current: true tačno
          // jednom po usluzi, opis već formatiran ("12,50 %").
          label: "U6 GET cjenovnik restorana 175",
          method: "GET",
          url: "restaurants/175/pricing",
        },
        {
          // Nova cijena — zatvara trenutno važeću, ne mijenja je. Zamijeni
          // service_id stvarnim iz U5.
          label: "U7 POST nova cijena (restoran 175)",
          method: "POST",
          url: "restaurants/175/pricing",
          body: { service_id: 1, paying_type: 2, amount: 14.0, valid_from: null },
        },
        {
          // Potvrda zatečene (is_verified: false) cijene iz starog sistema.
          // Zamijeni <PRICING_ID> ID-jem reda sa is_verified: false iz U6.
          label: "U8 PATCH potvrdi cijenu <PRICING_ID>",
          method: "PATCH",
          url: "restaurants/175/pricing/<PRICING_ID>/verify",
        },
        {
          // Globalni radni spisak za cjenovnik — grupe NEDOSTAJE CJENOVNIK /
          // CJENOVNIK NIJE POTVRĐEN / UGOVOR ISTEKAO / UGOVOR USKORO ISTIČE.
          label: "U9 GET radni spisak cjenovnika (pending-verification)",
          method: "GET",
          url: "pricing/pending-verification",
        },
        // Firme za dostavu (12.09) — vidi docs/2026/09/12_09_2026_Network_checklist_firme_dostava.md
        // i 12_09_2026_Backend_odgovor_firme_dostava.textile (sekcija 0b). Test
        // nalog: restoran 175 ("Roštiljnica Laguna"), firma 24 ("Ordera
        // dostava"), TUĐI restoran 226 ("AGI PASTA AWAY") na istoj firmi.
        {
          // Snimak trenutnog stanja prije/poslije D2 — uporedi restaurant_id 226
          // red u restaurant_delivery_company (paying_type/paying/updated_at).
          label: "D1 GET firme za dostavu (companies-delivery)",
          method: "GET",
          url: "companies/companies-delivery",
        },
        {
          // Regresija — legitiman save sopstvenog reda, TAČAN oblik kao
          // stvarna komponenta (RestaurantOwnDeliverySettingsDialog.vue
          // save(), red 154-162): `id` uvijek jednako `restaurant_id`
          // (oboje = $store.state.restaurant.id), i to je jedini oblik koji
          // ta komponenta ikad stvarno šalje. Mora raditi bez greške.
          label: "D2 POST sopstveni red 175 na firmu 24 (regresija, tačan oblik iz UI-ja)",
          method: "POST",
          url: "companies/companies-delivery-restaurant",
          body: {
            restorani: [
              {
                id: 175,
                restaurant_id: 175,
                active_restoran: true,
                active_company: true,
                paying_type: 1,
                paying: "100.000",
                internal: false,
              },
            ],
            delivery_company: { id: 24 },
            restoran_id: 175,
          },
        },
        {
          // BOLA write retest (sekcija 0b) — poslato kao restoran 175 (pravi
          // `restoran_id: 175`), ali red u nizu cilja TUĐI restaurant_id 226
          // na firmi 24 (sa `id: 226` isto, tačan oblik kao da je red za
          // restoran 226). Backend tvrdi da filtrira svaki red po pozivaocu i
          // tiho ignoriše tuđe (ne 422). Očekivano poslije fixa: 200 OK, ALI
          // D1 poslije MORA pokazati NEPROMIJENJEN red 226 (isti
          // paying_type/paying/updated_at kao prije ovog poziva).
          label: "D3 POST tuđi restaurant_id 226 na firmu 24, pravi restoran_id: 175 (BOLA write retest)",
          method: "POST",
          url: "companies/companies-delivery-restaurant",
          body: {
            restorani: [
              {
                id: 226,
                restaurant_id: 226,
                active_restoran: true,
                active_company: true,
                paying_type: 3,
                paying: 77777,
                internal: false,
              },
            ],
            delivery_company: { id: 24 },
            restoran_id: 175,
          },
        },
        {
          // KRITIČNIJI test od D3 — provjerava da li backend uzima "ko je
          // pozivalac" IZ AUTENTIFIKACIJE (token/sesija) ili NAIVNO vjeruje
          // `restoran_id`/`id` poljima iz tijela zahtjeva. Ovdje smo i dalje
          // ulogovani kao restoran 175, ali CIJELO tijelo (red u nizu I
          // restoran_id) LAŽIRA da smo restoran 226 — tačan oblik koji bi
          // proizvela komponenta DA je ulogovani restoran zaista 226. Ako
          // 226-ov red PROMIJENI vrijednost poslije ovoga — server-side
          // filter se oslanja na tijelo zahtjeva, ne na autentifikaciju, fix
          // je trivijalno zaobilazan i treba HITNO nazad backendu. Ako
          // OSTANE nepromijenjen — filter stvarno koristi autentifikovanog
          // korisnika.
          label: "D4 POST isto kao D3, ali CIJELO tijelo lažira restoran 226 (spoofing test)",
          method: "POST",
          url: "companies/companies-delivery-restaurant",
          body: {
            restorani: [
              {
                id: 226,
                restaurant_id: 226,
                active_restoran: true,
                active_company: true,
                paying_type: 3,
                paying: 88888,
                internal: false,
              },
            ],
            delivery_company: { id: 24 },
            restoran_id: 226,
          },
        },
      ],
    };
  },
  computed: {
    bodyAllowed() {
      return this.method !== "GET";
    },
    bodyError() {
      if (!this.bodyAllowed || !this.body.trim()) return false;
      try {
        JSON.parse(this.body);
        return false;
      } catch (e) {
        return true;
      }
    },
    hasToken() {
      return !!window.localStorage.getItem("restoran-token");
    },
    userType() {
      return this.$store.state.user.user && this.$store.state.user.user.type;
    },
    companyId() {
      return this.$store.state.company && this.$store.state.company.id;
    },
    restaurantId() {
      return this.$store.state.restaurant && this.$store.state.restaurant.id;
    },
    statusColor() {
      if (!this.response) return "inherit";
      const s = this.response.status;
      if (s >= 200 && s < 300) return "#2e7d32";
      if (s >= 400) return "#c62828";
      return "#ef6c00";
    },
    prettyBody() {
      if (!this.response) return "";
      try {
        return JSON.stringify(this.response.data, null, 2);
      } catch (e) {
        return String(this.response.data);
      }
    },
    prettyHeaders() {
      if (!this.response) return "";
      return JSON.stringify(this.response.headers, null, 2);
    },
  },
  created() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(LS_KEY) || "null");
      if (saved) {
        this.method = saved.method || "GET";
        this.url = saved.url || "";
        this.body = saved.body || "";
      }
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    applyPreset(p) {
      this.method = p.method;
      this.url = p.url;
      this.body = p.body ? JSON.stringify(p.body, null, 2) : "";
    },
    restore(h) {
      this.method = h.method;
      this.url = h.url;
      this.body = h.body || "";
    },
    async send() {
      if (!this.url.trim()) return;
      if (this.bodyError) return;

      this.loading = true;
      this.response = null;
      this.errorText = "";

      const path = this.url.trim().replace(/^\//, "");
      let data;
      if (this.bodyAllowed && this.body.trim()) {
        data = JSON.parse(this.body);
      }

      window.localStorage.setItem(
        LS_KEY,
        JSON.stringify({ method: this.method, url: this.url, body: this.body })
      );

      const t0 = performance.now();
      try {
        const cfgReq = { method: this.method.toLowerCase(), url: path };
        if (data !== undefined) cfgReq.data = data;
        const res = await axiosInstance.request(cfgReq);
        this.setResponse(res, t0, true);
      } catch (err) {
        if (err.response) {
          this.setResponse(err.response, t0, false);
        } else {
          this.errorText =
            (err && err.message) ||
            "Nema odgovora (mreža / CORS / zahtjev prekinut).";
          this.pushHistory({ status: "ERR", ok: false, ms: Math.round(performance.now() - t0) });
        }
      } finally {
        this.loading = false;
      }
    },
    setResponse(res, t0, ok) {
      const ms = Math.round(performance.now() - t0);
      let size = "—";
      try {
        size = new Blob([JSON.stringify(res.data)]).size + " B";
      } catch (e) {
        /* ignore */
      }
      this.response = {
        status: res.status,
        statusText: res.statusText || "",
        ms,
        size,
        data: res.data,
        headers: res.headers || {},
      };
      this.pushHistory({ status: res.status, ok, ms });
    },
    pushHistory(extra) {
      this.history.unshift({
        method: this.method,
        url: this.url,
        body: this.body,
        ...extra,
      });
      if (this.history.length > 20) this.history.pop();
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        /* clipboard blocked — ignore */
      }
    },
  },
};
</script>

<style scoped>
.resp {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border-radius: 4px;
  overflow: auto;
  max-height: 480px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}
.hist {
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}
.hist:hover {
  background: rgba(0, 0, 0, 0.03);
}
code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
